/**
 * Team play in the game screen (docs/team-games-plan.md §9 "In game").
 *
 * Pure helpers over the shared predicates in `@warcarp/api-types` (`isAlly`,
 * `canPlaceOn`, `areAllies`, `teamOf`). Every helper is gated on
 * `state.teams`: without it the game is free-for-all and each one returns
 * exactly the old "owned by me" answer, so individual games are unchanged.
 */
import { areAllies, borderReach, canPlaceOn, isAlly, isTeamGame, teamLabel, teamOf, type PlayerId, type TeamId, type TeamMembership } from '@warcarp/api-types'

interface TeamTerritory {
  id: string
  ownerId?: string | null
  armies: number
}

interface BorderTerritory extends TeamTerritory {
  adjacencies: string[]
  oneWayBorders?: string[]
}

interface TeamBoard<T extends TeamTerritory = TeamTerritory> {
  teams?: TeamMembership
  players?: readonly PlayerId[]
  map: { territories: readonly T[] }
}

/**
 * Territories the viewer may place reinforcements on: their own, plus a
 * teammate's in a team game (`canPlaceOn`). Free-for-all: exactly their own.
 * Placement, post-elimination placement and the troop-cap budget all read
 * this, matching the server's `canReinforce`.
 */
export function placeableTerritories<T extends TeamTerritory>(state: TeamBoard<T>, myPlayerId: PlayerId): T[] {
  return state.map.territories.filter((t) => canPlaceOn(state, myPlayerId, t))
}

/**
 * Fortify sources: the viewer's OWN territories with an army to spare and a
 * border into a territory the viewer or a teammate holds. The source is never
 * a teammate's (the server requires `from` to be yours).
 */
export function fortifySources<T extends BorderTerritory>(state: TeamBoard<T>, myPlayerId: PlayerId): T[] {
  const byId = new Map(state.map.territories.map((t) => [t.id, t]))
  return state.map.territories.filter((t) => {
    if (t.ownerId !== myPlayerId || t.armies < 2) return false
    return borderReach(t).some((id) => isAlly(state, myPlayerId, byId.get(id)?.ownerId))
  })
}

/** Fortify destinations from `from`: bordering territories held by the viewer or a teammate. */
export function fortifyDestinationIds(state: TeamBoard, myPlayerId: PlayerId, from: BorderTerritory | null): string[] {
  if (!from) return []
  const byId = new Map(state.map.territories.map((t) => [t.id, t]))
  return borderReach(from).filter((id) => isAlly(state, myPlayerId, byId.get(id)?.ownerId))
}

/**
 * True when the territory belongs to one of the viewer's teammates (not the
 * viewer). Attacking it is legal but the attack popup warns first. Always
 * false in free-for-all.
 */
export function isTeammateTerritory(
  state: { teams?: TeamMembership } | null | undefined,
  myPlayerId: PlayerId | null | undefined,
  territory: { ownerId?: string | null } | null | undefined,
): boolean {
  return areAllies(state, myPlayerId, territory?.ownerId ?? null)
}

/**
 * The attack target to pick automatically once a source is chosen, or null.
 * Free-for-all: the lone valid target, as before. Team games skip teammates,
 * so a tap never lands on an ally by default: the lone NON-teammate target is
 * picked, and a source whose only target is a teammate picks nothing.
 */
export function autoAttackTargetId(state: TeamBoard, myPlayerId: PlayerId, validTargetIds: readonly string[]): string | null {
  const byId = new Map(state.map.territories.map((t) => [t.id, t]))
  const candidates = validTargetIds.filter((id) => !isTeammateTerritory(state, myPlayerId, byId.get(id)))
  return candidates.length === 1 ? candidates[0]! : null
}

/**
 * Fog of war: the slots on the viewer's side, whose hands and totals the
 * viewer knows. The server keeps only the side's hands, so the side is the
 * hands present; in a team game it is widened to every member of those teams
 * (a team shares one view, plan §12 decision 11). Null when fog is off.
 */
export function fogViewerSide(
  state: { teams?: TeamMembership; cards?: Record<string, unknown> | null },
  fogActive: boolean,
): Set<string> | null {
  if (!fogActive) return null
  const side = new Set(Object.keys(state.cards ?? {}))
  const teams = state.teams
  if (teams) {
    const sideTeams = new Set([...side].map((slot) => teams[slot]).filter((team): team is TeamId => !!team))
    for (const [slot, team] of Object.entries(teams)) if (sideTeams.has(team)) side.add(slot)
  }
  return side
}

/** One team's block in the player drawer. */
export interface TeamGroup<P> {
  team: TeamId
  members: P[]
}

/**
 * Group drawer rows under their teams, keeping turn order: teams appear in
 * the order their first member moves, and members keep their turn-order
 * position inside the team. Rows with no team (never in a started team game)
 * are dropped into a trailing group of their own. Returns null in
 * free-for-all, so callers render the flat list unchanged.
 */
export function groupByTeam<P>(
  state: { teams?: TeamMembership } | null | undefined,
  rows: readonly P[],
  playerIdOf: (row: P) => PlayerId,
): TeamGroup<P>[] | null {
  if (!isTeamGame({ state })) return null
  const groups: TeamGroup<P>[] = []
  for (const row of rows) {
    const team = teamOf(state, playerIdOf(row))
    if (!team) continue
    let group = groups.find((g) => g.team === team)
    if (!group) {
      group = { team, members: [] }
      groups.push(group)
    }
    group.members.push(row)
  }
  return groups
}

/** Combined figures for a drawer team header. */
export interface TeamTotals {
  troops: number
  territories: number
  /** Members still in the game. */
  alive: number
  /** True when fog hides some of this team's board from the viewer. */
  partial: boolean
}

export function teamTotals(members: ReadonlyArray<{ totalTroops: number; owned: number; eliminated: boolean; fog?: unknown }>): TeamTotals {
  return members.reduce<TeamTotals>((acc, m) => ({
    troops: acc.troops + m.totalTroops,
    territories: acc.territories + m.owned,
    alive: acc.alive + (m.eliminated ? 0 : 1),
    partial: acc.partial || !!m.fog,
  }), { troops: 0, territories: 0, alive: 0, partial: false })
}

/**
 * Teammates whose hands the viewer may read (plan §12 decision 10), in turn
 * order. Empty in free-for-all, for spectators, and for hands the server did
 * not send (fog of war keeps only the viewer's side).
 */
export function teammateHands<C>(
  state: { teams?: TeamMembership; players?: readonly PlayerId[]; cards?: Record<string, C[]> | null },
  myPlayerId: PlayerId | null | undefined,
): Array<{ playerId: PlayerId; cards: C[] }> {
  if (!myPlayerId || !isTeamGame({ state })) return []
  const order = state.players ?? Object.keys(state.teams ?? {})
  return order
    .filter((p) => areAllies(state, myPlayerId, p) && Array.isArray(state.cards?.[p]))
    .map((p) => ({ playerId: p, cards: state.cards![p]! }))
}

/** What the drawer's share bar measures (decision 4's tiebreak measures). */
export type TeamShareMeasure = 'territories' | 'troops' | 'income'
export const TEAM_SHARE_MEASURES: readonly TeamShareMeasure[] = ['territories', 'troops', 'income']

/** The roster figures the share bar reads (a `PlayerInfo`). */
interface ShareMember {
  owned: number
  totalTroops: number
  nextReinforce: number
  fog?: { ownershipPublic: boolean }
}

/**
 * Measures the viewer knows for every team. Fog of war hides other sides'
 * troops and income (and, in heavy fog, their territory count), and a share of
 * a partly hidden board would mislead, so those measures are not offered.
 * Without fog every measure is known.
 */
export function knownShareMeasures(groups: ReadonlyArray<TeamGroup<ShareMember>>): TeamShareMeasure[] {
  const members = groups.flatMap((g) => g.members)
  if (members.every((m) => !m.fog)) return [...TEAM_SHARE_MEASURES]
  return members.every((m) => !m.fog || m.fog.ownershipPublic) ? ['territories'] : []
}

/** One team's slice of the share bar. */
export interface TeamShare {
  team: TeamId
  value: number
}

/** Each team's total for `measure`, in the groups' order. */
export function teamShares(groups: ReadonlyArray<TeamGroup<ShareMember>>, measure: TeamShareMeasure): TeamShare[] {
  const of = (m: ShareMember) => (measure === 'territories' ? m.owned : measure === 'troops' ? m.totalTroops : m.nextReinforce)
  return groups.map((g) => ({ team: g.team, value: g.members.reduce((sum, m) => sum + of(m), 0) }))
}

/**
 * The share bar's one-line summary: who leads and by how much, then the
 * viewer's team. "Team A leads by 3 · you are Team A". Ties name every team
 * level at the top ("Team A and Team B tie for the lead"; "All teams are
 * level" when nobody is ahead). Spectators get no "you are" clause.
 */
export function teamShareSummary(shares: readonly TeamShare[], viewerTeam: TeamId | null): string {
  const you = viewerTeam ? ` · you are ${teamLabel(viewerTeam)}` : ''
  if (shares.length === 0) return ''
  const sorted = [...shares].sort((a, b) => b.value - a.value)
  const top = sorted[0]!.value
  const leaders = sorted.filter((s) => s.value === top).map((s) => teamLabel(s.team))
  if (leaders.length === shares.length) return `All teams are level${you}`
  if (leaders.length > 1) {
    const names = leaders.length === 2 ? leaders.join(' and ') : `${leaders.slice(0, -1).join(', ')} and ${leaders.at(-1)}`
    return `${names} tie for the lead${you}`
  }
  return `${leaders[0]} leads by ${top - sorted[1]!.value}${you}`
}

/** The viewer's team first, then the rest in their existing (turn) order. */
export function viewerTeamFirst<G extends { team: TeamId }>(groups: readonly G[], viewerTeam: TeamId | null): G[] {
  if (!viewerTeam) return [...groups]
  return [...groups.filter((g) => g.team === viewerTeam), ...groups.filter((g) => g.team !== viewerTeam)]
}
